import axios from "./../axios/axios";
import { AnyAction, ThunkAction } from "@reduxjs/toolkit";
import { RootState } from "./index";
import { authActions } from "./auth-slice";
import { baseUrl } from "../axios/axios";
import { validateToken, getTokenAuthorities } from "../utils/tokenUtils";

export const login = (
  credentials: { email: string | null; password: string | null },
  refreshLogin = false,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    dispatch(authActions.loginStart());

    //Disable for deploy
    // const url = 'http://localhost:8081/auth/oauth/token'

    //Enable for deploy
    // const url = 'https://apoteka.rs:8443/authorization_server/oauth/token';

    //Enable for deploy for dev server
    const url = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL + "/authorization_server/oauth/token"
      : "https://test.apoteka.rs:8443/authorization_server/oauth/token";

    const params = new URLSearchParams();
    if (refreshLogin) {
      params.append("grant_type", "refresh_token");
      params.append(
        "refresh_token",
        localStorage.getItem("refreshToken") || "",
      );
    } else {
      params.append("grant_type", "password");
      params.append("username", credentials.email ? credentials.email : "null");
      params.append(
        "password",
        credentials.password ? credentials.password : "null",
      );
    }

    try {
      const response = await axios.post(url, params, {
        auth: {
          username: "apoteka_rs",
          password: "F7Q8T97@a'QveBX?",
        },
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      let approveLogin = false;
      let skipMarketplaceFetch = false;
      let isAdminUser = false; // Track if user is admin

      if (response.data.userAuthorities.includes("ADMIN")) {
        dispatch(authActions.setIsAdmin());
        approveLogin = true;
        isAdminUser = true; // Mark as admin user
        skipMarketplaceFetch = true;
      } else if (
        response.data.userAuthorities.includes("MARKETPLACE_ADMIN_USER")
      ) {
        dispatch(authActions.setIsMarketplaceAdmin());
        approveLogin = true;
        skipMarketplaceFetch = true;
      } else if (
        response.data.userAuthorities.includes("PHARMACIST_SUPPORT_USER")
      ) {
        dispatch(authActions.setIsPharmacist());
        approveLogin = true;
      } else if (
        response.data.userAuthorities.includes("PRODAJNO_MESTO_USER")
      ) {
        dispatch(authActions.setRegularUser());
        approveLogin = true;
      } else if (response.data.userAuthorities.includes("DELIVERY_USER")) {
        dispatch(authActions.setIsDriver());
        approveLogin = true;
      } else {
        dispatch(authActions.loginFailed());
      }

      // dozvoli login samo ako ima neku od navedenih rola
      if (approveLogin) {
        const expirationDate = new Date(
          new Date().getTime() + response.data.expires_in * 1000,
        );

        // Clear marketplace info for non-admin users only
        if (!refreshLogin && !isAdminUser) {
          localStorage.removeItem("marketplaceId");
          localStorage.removeItem("branchMarketplaceId");
        }

        localStorage.setItem("token", response.data.access_token);
        localStorage.setItem("refreshToken", response.data.refresh_token);
        localStorage.setItem("expirationDate", expirationDate.toString());
        localStorage.setItem("id", response.data.userDetails.id);
        localStorage.setItem("email", response.data.userDetails.username);
        localStorage.setItem("firstName", response.data.userDetails.firstName);
        localStorage.setItem(
          "secondName",
          response.data.userDetails.secondName,
        );
        localStorage.setItem("roles", response.data.userAuthorities);

        dispatch(
          authActions.userInfoFn({
            id: response.data.userDetails.id,
            username: response.data.userDetails.username,
            firstName: response.data.userDetails.firstName,
            secondName: response.data.userDetails.secondName,
            roles: response.data.userAuthorities,
          }),
        );

        dispatch(authActions.setIsInitialLogin(!refreshLogin));
        dispatch(authActions.loginSuccess(response.data.access_token));

        // Fetch marketplace info for non-admin users during both fresh login and token refresh
        if (!skipMarketplaceFetch) {
          console.log(
            `Auth: Fetching marketplace info for non-admin user (${
              refreshLogin ? "token refresh" : "fresh login"
            })`,
          );
          await dispatch(
            fetchMarketplaceInfoOnLogin(response.data.userDetails.id),
          );
        } else {
          console.log("Auth: Skipping marketplace info fetch for admin user");
        }

        dispatch(checkLoginTimeout(response.data.expires_in));
        dispatch(authActions.setAuthInitialized(true));
      }
    } catch (error: any) {
      console.error("Auth: Login error:", error);

      if (refreshLogin) {
        // Refresh token failed - clear all auth state and redirect to login
        console.error(
          "Auth: Refresh token failed, clearing auth state and logging out",
          error,
        );
        dispatch(authActions.logout());
        dispatch(authActions.setAuthInitialized(true));
        return;
      }

      // Regular login error handling
      if (error.response?.data?.error_description === "Bad credentials") {
        console.error("Auth: Bad credentials provided");
        dispatch(authActions.loginFailed());
      } else {
        console.error("Auth: Login failed with server error");
        dispatch(authActions.loginFailed());
      }

      dispatch(authActions.setAuthInitialized(true));
    }
  };
};

export const fetchMarketplaceInfo = (
  user_id: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    console.log(`Auth: Fetching marketplace info for user ${user_id}`);
    dispatch(authActions.fetchMarketplaceInfoStart());

    try {
      const response = await axios.get(
        baseUrl + "/marketplace/user/" + user_id,
        {
          params: { access_token: localStorage.getItem("token") },
        },
      );

      console.log("Auth: Marketplace info fetched successfully", response.data);
      dispatch(authActions.marketplaceInfo(response.data));
      localStorage.setItem("marketplaceId", response.data.marketplaceId);
      localStorage.setItem(
        "branchMarketplaceId",
        response.data.branchMarketplaceId,
      );
    } catch (error: any) {
      console.error("Auth: Failed to fetch marketplace info", error);

      // Handle authentication errors specifically
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error(
          "Auth: Marketplace info fetch failed due to authentication error, token is invalid",
        );
        // The global interceptor will handle the logout, but let's also dispatch it here for safety
        dispatch(authActions.logout());
      } else {
        console.error(
          "Auth: Marketplace info fetch failed with non-auth error:",
          error.response?.status,
        );
      }

      dispatch(authActions.fetchMarketplaceInfoFailed(error));
    }
  };
};

export const fetchMarketplaceInfoOnLogin = (
  user_id: number,
): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    dispatch(authActions.fetchMarketplaceInfoStart());

    try {
      const response = await axios.get(
        baseUrl + "/marketplace/user/" + user_id,
        {
          params: { access_token: localStorage.getItem("token") },
        },
      );

      // Validate response data before using
      if (!response.data.branchMarketplaceId || !response.data.marketplaceId) {
        throw new Error("Invalid marketplace info received from server");
      }

      // Set localStorage AND Redux state atomically
      localStorage.setItem("marketplaceId", response.data.marketplaceId);
      localStorage.setItem(
        "branchMarketplaceId",
        response.data.branchMarketplaceId,
      );

      dispatch(authActions.marketplaceInfo(response.data));

      // Log successful marketplace info fetch
      console.log("Auth: Marketplace info fetched successfully", {
        branchMarketplaceId: response.data.branchMarketplaceId,
        marketplaceId: response.data.marketplaceId,
      });

      // NEW: Also fetch detailed marketplace info for license data
      await dispatch(
        fetchMarketplaceInfoDetailed(
          response.data.branchMarketplaceId,
          response.data.marketplaceId,
        ),
      );

      // Set active status after marketplace info is confirmed
      dispatch(
        setCNCActiveStatus(
          response.data.branchMarketplaceId,
          response.data.marketplaceId,
          1,
        ),
      );
    } catch (error: any) {
      console.error(
        "Auth: Failed to fetch marketplace info for non-admin user",
        error,
      );
      dispatch(authActions.fetchMarketplaceInfoFailed(error));
    }
  };
};

export const logoutAction = (
  branchMarketplaceId: number | null,
  marketplaceId: number | null,
  activeStatus: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const URL =
      "/marketplace/takes_orders/" +
      branchMarketplaceId +
      "/" +
      marketplaceId +
      "?takesOrders=" +
      activeStatus;
    axios
      .put(URL)
      .then((response) => {
        dispatch(authActions.setActiveStatus(activeStatus));
        dispatch(authActions.logout());
      })
      .catch((error) => {
        console.log("Error se desio" + error);
        // dispatch(orderActions.fetchNewOrderFailed())
      });
  };
};

export const setCNCActiveStatus = (
  branchMarketplaceId: number,
  marketplaceId: number,
  activeStatus: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    const URL =
      "/marketplace/takes_orders/" +
      branchMarketplaceId +
      "/" +
      marketplaceId +
      "?takesOrders=" +
      activeStatus;
    axios
      .put(URL)
      .then((response) => {
        dispatch(authActions.setActiveStatus(activeStatus));
      })
      .catch((error) => {
        console.log("Error se desio" + error);
        // dispatch(orderActions.fetchNewOrderFailed())
      });
  };
};

export const pingActiveStatus = (
  branchMarketplaceId: number,
  marketplaceId: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    // Use takes_orders endpoint to prevent clearing active pharmacy license
    const URL = `/marketplace/takes_orders/${branchMarketplaceId}/${marketplaceId}?takesOrders=1`;
    axios
      .put(URL)
      .then(() => {})
      .catch((error) => {
        console.log("Keep-alive ping error: " + error);
      });
  };
};

export const fetchMarketplaceInfoDetailed = (
  branchMarketplaceId: number,
  marketplaceId: number,
): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => {
  return async (dispatch) => {
    dispatch(authActions.fetchMarketplaceInfoDetailedStart());

    try {
      const response = await axios.get(
        `${baseUrl}/marketplace/${marketplaceId}/${branchMarketplaceId}`,
      );

      dispatch(authActions.setMarketplaceInfoDetailed(response.data));

      console.log(
        "Auth: Detailed marketplace info with license fetched successfully",
      );
    } catch (error: any) {
      console.error("Auth: Failed to fetch detailed marketplace info", error);
      dispatch(authActions.fetchMarketplaceInfoDetailedFailed());
    }
  };
};

export const checkLoginTimeout = (
  time: number,
): ThunkAction<void, RootState, unknown, AnyAction> => {
  return (dispatch) => {
    setTimeout(() => {
      if (
        localStorage.getItem("refreshToken") === null ||
        localStorage.getItem("refreshToken") === ""
      ) {
        let branchMarketplaceId: any = localStorage.getItem(
          "branchMarketplaceId",
        );
        let marketplaceId: any = localStorage.getItem("marketplaceId");
        dispatch(logoutAction(branchMarketplaceId, marketplaceId, 0));
        dispatch(authActions.logout());
      } else {
        dispatch(login({ email: null, password: null }, true));
      }
    }, time * 1000);
  };
};

export const loginCheckState = (): ThunkAction<
  Promise<void>,
  RootState,
  unknown,
  AnyAction
> => {
  return async (dispatch) => {
    console.log("Auth: Initializing authentication state check");

    const token = localStorage.getItem("token");

    if (!token) {
      console.log("Auth: No token found, logging out");
      dispatch(authActions.logout());
      dispatch(authActions.setAuthInitialized(true));
      return;
    }

    // Client-side token validation
    const tokenValidation = validateToken(token);

    if (!tokenValidation.hasValidFormat) {
      console.error("Auth: Invalid token format, logging out");
      dispatch(authActions.logout());
      dispatch(authActions.setAuthInitialized(true));
      return;
    }

    if (tokenValidation.isExpired) {
      console.log("Auth: Token is expired, attempting refresh");
      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        console.log("Auth: No refresh token available, logging out");
        dispatch(authActions.logout());
        dispatch(authActions.setAuthInitialized(true));
        return;
      }

      // Attempt refresh
      dispatch(login({ email: null, password: null }, true));
      // Note: authInitialized will be set in login() after completion
      return;
    }

    // Token is valid, restore auth state
    console.log("Auth: Valid token found, restoring session");
    dispatch(authActions.setIsInitialLogin(false));
    dispatch(authActions.loginSuccess(token));

    if (localStorage.getItem("id")) {
      dispatch(
        authActions.userInfoFn({
          id: localStorage.getItem("id")!,
          username: localStorage.getItem("email")!,
          firstName: localStorage.getItem("firstName")!,
          secondName: localStorage.getItem("secondName")!,
          roles: localStorage.getItem("roles")!.split(","),
        }),
      );

      // Restore role flags from localStorage and track admin status
      const roles = getTokenAuthorities(token);
      let skipMarketplaceFetch = false;
      if (roles.includes("ADMIN")) {
        dispatch(authActions.setIsAdmin());
        skipMarketplaceFetch = true;
      } else if (roles.includes("MARKETPLACE_ADMIN_USER")) {
        dispatch(authActions.setIsMarketplaceAdmin());
        skipMarketplaceFetch = true;
      } else if (roles.includes("PHARMACIST_SUPPORT_USER")) {
        dispatch(authActions.setIsPharmacist());
      } else if (roles.includes("PRODAJNO_MESTO_USER")) {
        dispatch(authActions.setRegularUser());
      } else if (roles.includes("DELIVERY_USER")) {
        dispatch(authActions.setIsDriver());
      }

      // BACKGROUND DATA LOADING: Fetch marketplace info without blocking UI
      // This restores the missing data initialization while keeping the app responsive
      const userId = localStorage.getItem("id");
      if (!skipMarketplaceFetch && userId) {
        const parsedId = parseInt(userId, 10);
        if (!isNaN(parsedId)) {
          console.log(
            "Auth: Starting background marketplace info fetch during session restoration",
          );

          // Background fetch without blocking auth initialization
          const backgroundFetch = async () => {
            try {
              await dispatch(fetchMarketplaceInfoOnLogin(parsedId));
              console.log(
                "Auth: Background marketplace info fetch completed successfully",
              );
            } catch (error: any) {
              console.error(
                "Auth: Background marketplace info fetch failed",
                error,
              );
              // Could implement retry logic or user notification here if needed
            }
          };
          backgroundFetch(); // Fire and forget
        }
      } else if (skipMarketplaceFetch) {
        console.log(
          "Auth: Skipping marketplace info fetch for admin user during session restoration",
        );
      }
    }

    // Set up timeout based on actual token expiration
    const expirationDate = new Date(
      localStorage.getItem("expirationDate") || "",
    );
    const timeUntilExpiry = Math.max(
      0,
      (expirationDate.getTime() - new Date().getTime()) / 1000,
    );

    dispatch(checkLoginTimeout(timeUntilExpiry));

    // OPTIMISTIC AUTH INITIALIZATION: Set auth initialized immediately
    // This eliminates the AuthLoadingSpinner delay by separating auth validation from app data fetching
    dispatch(authActions.setAuthInitialized(true));
  };
};
